import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import MathRender from "@/components/Math";
import { ArrowLeft, PlayCircle, Sparkles, Target } from "lucide-react";

export default function LearnSkill() {
  const { skillId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/learn/skills/${skillId}`).then((r) => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [skillId]);

  if (loading) return <div className="text-zinc-500 text-sm" data-testid="learn-skill-loading">Loading skill…</div>;
  if (!data) return <div className="text-red-300 text-sm">Skill not found.</div>;

  const { course, unit, skill, questions } = data;
  const hasQuestions = questions.length > 0;

  return (
    <div data-testid="learn-skill-page" className="max-w-3xl">
      <Link
        to={`/learn/unit/${unit.id}`}
        className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-yellow-300 mb-4"
        data-testid="learn-back-unit-from-skill"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to {unit.title}
      </Link>

      <div className="mb-6 fade-up">
        <div className="text-[10px] uppercase tracking-[0.2em] text-pink-300 mb-1 flex items-center gap-1.5">
          <Target className="w-3 h-3" /> Skill · {course.title}
        </div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">{skill.title}</h1>
        <p className="text-zinc-400 mt-1 text-sm">
          Unit {unit.number} · {unit.title}
        </p>
      </div>

      {/* Practice launcher */}
      {hasQuestions ? (
        <Link
          to={`/learn/skill/${skill.id}/practice`}
          data-testid="learn-skill-start-practice"
          className="fade-up flex items-center justify-between rounded-lg border border-yellow-300/30 bg-gradient-to-r from-yellow-300/10 via-orange-300/5 to-pink-300/5 p-5 mb-6 hover:border-yellow-300/60 transition-colors group"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-yellow-300 mb-1">Start practice</div>
            <div className="font-display text-lg font-semibold text-zinc-100">
              Practise this skill — {questions.length} question{questions.length === 1 ? "" : "s"}
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">
              4-rung hint ladder, misconception check, and AI Tutor on wrong answers.
            </div>
          </div>
          <PlayCircle className="w-8 h-8 text-yellow-300 group-hover:scale-110 transition-transform" />
        </Link>
      ) : (
        <div
          className="fade-up flex items-center gap-3 rounded-lg border border-orange-300/25 bg-orange-300/5 p-5 mb-6"
          data-testid="learn-skill-coming-soon"
        >
          <Sparkles className="w-5 h-5 text-orange-300 shrink-0" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-orange-300 mb-1">Practice bank in progress</div>
            <div className="text-sm text-zinc-200">
              We're hand-crafting questions for this skill. Read the lessons above in the meantime.
            </div>
          </div>
        </div>
      )}

      {/* Preview questions (if we have any) */}
      {hasQuestions && (
        <section data-testid="learn-skill-preview">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-3">
            Question preview ({Math.min(3, questions.length)} of {questions.length})
          </div>
          <ol className="space-y-3">
            {questions.slice(0, 3).map((q, i) => (
              <li
                key={q.id}
                className="rounded-lg border border-white/10 bg-[#152040] p-4 text-sm text-zinc-200"
                data-testid={`learn-skill-question-${i + 1}`}
              >
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-[10px] font-mono text-yellow-300">Q{i + 1}</span>
                </div>
                <MathRender>{q.stem}</MathRender>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
